import React, { useEffect, useState } from "react";
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  PublicClientApplication,
  InteractionStatus,
  EventType,
  AuthenticationResult,
} from "@azure/msal-browser";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { msalConfig, loginRequest } from "./authConfig";
import {
  reactPlugin,
  trackEvent,
  trackException,
  trackTrace,
  setAuthenticatedUser,
  SeverityLevel,
} from "./telemetry/telemetryService";
import AskHrTicket from "./AskHrTicket";
import "./App.css";

// ─── Create the MSAL instance once ────────────────────────────
const msalInstance = new PublicClientApplication(msalConfig);

// ─── Bootstrap: initialize + handle redirect BEFORE React renders ──
// This promise must resolve before any login interaction is attempted.
const msalReady = msalInstance.initialize().then(() => {
  return msalInstance.handleRedirectPromise().then((response) => {
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }
    }
  }).catch((error) => {
    // If a stale/bad auth code is stuck in state, clear it and start fresh
    trackException(
      error instanceof Error ? error : new Error(String(error)),
      SeverityLevel.Warning,
      { source: "MSAL", event: "RedirectHandlingFailed" },
    );
    trackTrace("Redirect handling failed, clearing MSAL state", SeverityLevel.Warning, {
      source: "MSAL",
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    // Clear any MSAL-related keys from sessionStorage (avoid wiping non-MSAL state)
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("msal."))
      .forEach((key) => sessionStorage.removeItem(key));
    // Clear any MSAL-related keys from localStorage
    Object.keys(localStorage)
      .filter((key) => key.startsWith("msal."))
      .forEach((key) => localStorage.removeItem(key));
  });
});

// Whenever a login succeeds, set that account as active
msalInstance.addEventCallback((event) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS &&
    event.payload &&
    "account" in event.payload
  ) {
    const account = (event.payload as AuthenticationResult).account;
    if (account) {
      msalInstance.setActiveAccount(account);
      // Set App Insights authenticated user context for correlated telemetry
      setAuthenticatedUser(
        account.username || account.localAccountId,
        account.tenantId,
      );
      trackEvent("LoginSuccess", {
        userId: account.username || "",
        tenantId: account.tenantId || "",
      });
    }
  }
});

// ─── AutoLogin: waits for MSAL to be ready, then signs in ────
const AutoLogin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [ready, setReady] = useState(false);

  // Wait for handleRedirectPromise to finish before doing anything
  useEffect(() => {
    msalReady.then(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready) return; // MSAL hasn't finished processing the redirect yet
    if (isAuthenticated) return; // already signed in
    if (inProgress !== InteractionStatus.None) return; // another flow is active

    // Try silent SSO → if that fails, redirect to login
    instance
      .ssoSilent({ ...loginRequest })
      .catch(() => {
        // Don't set prompt — let Azure AD use the existing session automatically.
        // This avoids showing the email/account picker when the user is already
        // signed into a Microsoft account in this browser.
        void instance.loginRedirect(loginRequest).catch((redirectError) => {
          trackException(
            redirectError instanceof Error ? redirectError : new Error(String(redirectError)),
            SeverityLevel.Error,
            { source: "MSAL", event: "LoginRedirectFailed" },
          );
        });
      });
  }, [ready, isAuthenticated, inProgress, instance]);

  if (!ready) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
};

// ─── Loading screen shown while auth is in progress ──────────
const LoadingScreen: React.FC = () => (
  <div className="loading-screen">
    <div className="loading-spinner" />
    <p>Signing you in…</p>
  </div>
);

// ─── App ─────────────────────────────────────────────────────
function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppInsightsContext.Provider value={reactPlugin}>
      <AutoLogin>
        <AuthenticatedTemplate>
          <BrowserRouter>
            <Routes>
              <Route path="/FillAskHrTicket" element={<AskHrTicket />} />
              <Route path="/AddAttachment/FillAskHrTicket" element={<AskHrTicket />} />
              <Route
                path="*"
                element={
                  <div className="error-screen">
                    <div className="error-icon">⚠️</div>
                    <h2>This URL doesn't look right</h2>
                    <p>
                      It looks like you may have reached this page by mistake or
                      the link you used is incomplete. This page is meant to be
                      opened from a support ticket flow in MSX.
                    </p>
                    <p style={{ marginTop: 16 }}>
                      To get back on track, head over to{" "}
                      <a
                        href="https://microsoftsales.crm.dynamics.com/"
                        style={{ color: "#0063B1", fontWeight: 600 }}
                      >
                        Microsoft Sales (MSX)
                      </a>.
                    </p>
                  </div>
                }
              />
            </Routes>
          </BrowserRouter>
        </AuthenticatedTemplate>

        <UnauthenticatedTemplate>
          <LoadingScreen />
        </UnauthenticatedTemplate>
      </AutoLogin>
      </AppInsightsContext.Provider>
    </MsalProvider>
  );
}

export default App;
